import React, { FC, useState } from 'react';
import ReactQuill from 'react-quill';
import { FastField, FieldAttributes, FieldProps } from 'formik';
import styled from 'styled-components';

import { LoadingTexto, TextoMarkdown } from '../Tipografia/Texto';
import { useCampoFormularioProps } from 'service/restricao/useCampoFormularioProps/useCampoFormularioProps';

import 'react-quill/dist/quill.snow.css';


const MODULES = {
  toolbar: [
    [{ 'header': [2, 3, 4, false] }],
    ['bold', 'italic', 'underline','strike', 'blockquote'],
    [{'list': 'ordered'}, {'list': 'bullet'}],
    ['link'],
    ['clean']
  ],
  // Não prender o foco no componente
  keyboard: {
    bindings: {
      tab: {
        key: 9,
        handler: () => true
      }
    }
  }
}

const FORMATS = [
  'bold', 'code', 'italic', 'link', 'size', 'strike', 'script', 'underline',
  'blockquote', 'header', 'indent', 'list', 'align', 'code-block',
  // Desabilitados:
  // 'image', 'video'
]

interface EditorProps extends FieldAttributes<any> {
  /**
   * Obs: undefined é considerado aqui como true
   */
  isVinculadoFormik?: boolean
}

// TODO - Menção: https://github.com/afry/quill-mention
export const Editor: FC<EditorProps> = React.forwardRef<ReactQuill, EditorProps>(({isVinculadoFormik, ...props}, ref) => {
  const restricoesProps = useCampoFormularioProps(props.name, undefined);

  if (restricoesProps.hidden)
    return <></>

  if (restricoesProps.readOnly) {
    return (
      <FastField {...props}>
        {(formikProps: FieldProps) => <ConteudoVisualizacao {...props} formikProps={formikProps} />}
      </FastField>
    )
  }

  // Obs: ao contrário do Input, DEVE passar {...restricaoProps} no FastField para que o campo seja devidamente exibido
  return <FastField {...props} {...restricoesProps}>
    {(formikProps: FieldProps) => <ConteudoEdicao ref={ref} formikProps={formikProps} isVinculadoFormik={isVinculadoFormik} />}
  </FastField>
});


interface ConteudoVisualizacaoProps extends FieldAttributes<any> {
  formikProps: FieldProps;
}

const ConteudoVisualizacao: FC<ConteudoVisualizacaoProps> = ({formikProps, ...props}) => {
  const restricaoProps = useCampoFormularioProps(props.name, undefined);
  const valor = formikProps.meta.value;
  const hasValor = valor !== undefined && valor.trim() !== "";
  const carregando = restricaoProps.loading === true;

  if (!hasValor && carregando) {
    return <>
      <LoadingTexto width={450} />
      <LoadingTexto width={360} />
      <LoadingTexto width={120} />
    </>
  }
  
  return <TextoMarkdown className={props.className}>{valor}</TextoMarkdown>
}


interface EdicaoProps extends FieldAttributes<any> {
  formikProps: FieldProps;
  isVinculadoFormik?: boolean;
}


const ConteudoEdicao: FC<EdicaoProps> = React.forwardRef<ReactQuill, EdicaoProps>(({formikProps, isVinculadoFormik, ...props}, ref) => {
  const [, setValor] = useState("");
  const {field, form} = formikProps;
  const restricaoProps = useCampoFormularioProps(field.name, undefined);

  const estrategiaAtualizacao = isVinculadoFormik === false
    // Não atualiza formik e usuário é obrigado a pegar a referência
    ? (it: string) => setValor(it)
    // Atualiza formik.
    // Como é lento para formulários grandes, a verificação em cada
    // onChange foi desabilitada
    : (valor: string) => form.setFieldValue(field.name, valor, false);
  
  return <ReactQuillStyledContainer>
    <ReactQuill
      ref={ref}
      theme="snow"
      value={formikProps.field.value}
      // defaultValue={field.value}
      readOnly={restricaoProps.disabled || form.isSubmitting}
      modules={MODULES}
      formats={FORMATS}
      placeholder={props.placeholder}
      onBlur={() => form.setFieldTouched(field.name, true)}
      onChange={estrategiaAtualizacao}
    />
  </ReactQuillStyledContainer>
});


const ReactQuillStyledContainer = styled.div`
.quill {
  border: 2px solid ${({theme}) => theme.background.borda};
  border-radius: .25rem;

  transition: background-color 200ms ease-in-out, border-color 200ms ease-in-out;

  .ql-toolbar {
    border-top: none;
    border-right: none;
    border-left: none;
    border-bottom: 2px solid ${({theme}) => theme.background.borda};
  }

  .ql-container {
    border: none;
    transition: background-color 200ms ease-in-out;
  }

  .ql-disabled {
    color: ${({theme}) => theme.texto.desabilitado};
    
    background: ${({theme}) => theme.background.nivel_0};
  }

  .ql-editor p {
    /* Deve corresponder ao espaçamento de TextoMarkdown */
    margin-bottom: .25em;
  }
}
`;